import React, { useContext, useState, useEffect } from "react";
import { AuthContext } from "../contexts/auth";
import { Popover } from "@headlessui/react";
import Footer from "./footer";
import BackButton from "./back_button";
import { useParams, Link } from "wouter";
import { fetchEmployee, changeAppEnable } from "../services/api";
import { updateAnagraficaEmployee } from "../services/api_anagrafica_employee";
import Swal from "sweetalert2";



export default function EmployeeDetails() {
    const { profile, token } = useContext(AuthContext);
    const { codiceFiscale } = useParams();
    const [employee, setEmployee] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isError, setIsError] = useState(false);
    const [isEditingNotes, setIsEditingNotes] = useState(false);
    const [notesDraft, setNotesDraft] = useState("");
    const [isSavingNotes, setIsSavingNotes] = useState(false);

  
    useEffect(() => {
        const fetchData = async () => {
            setIsLoading(true);
            try {
                const employeeData = await fetchEmployee(token, codiceFiscale);

                setEmployee(employeeData);
                setNotesDraft(employeeData.employee?.notes ?? "");
                setIsEditingNotes(false);

                setIsError(false);
            } catch (error) {
                console.error('Error fetching data:', error);
                setIsError(true);
            } finally {
                setIsLoading(false);
            }
        };

        if (token) {
            fetchData();
        }
    }, [token, codiceFiscale]);

    const formatData = (data) => {
        if (!data) return '';
    
        const [anno, mese, giorno] = data.split('-');
        return `${giorno}/${mese}/${anno}`;
    };

    const handleAppStatusChange = async () => {
        const status = employee.employee.app_activate ? 'deactivate' : 'activate';
    
        if (!employee.employee.app_activate && !employee.employee.email_auth) {
            Swal.fire({
                icon: 'warning',
                title: 'Indirizzo Email mancante',
                text: 'Per attivare l\'accesso all\'app è necessario salvare la mail del dipendente.',
            });
            return;
        }
    
        if (!employee.employee.app_activate && !employee.employee.has_active_contract) {
            const result = await Swal.fire({
                icon: 'warning',
                title: 'Attenzione<br>Contratto Attivo non presente',
                html: `Attivando l'app non sarà possibile per il dipendente effettuare l'accesso in mancanza di un contratto attivo.<br>Inserisci il contratto per abilitare l'accesso.<br>Puoi effettuare questo passaggio anche successivamente.`,
                showCancelButton: true,
                showDenyButton: true,
                confirmButtonText: 'Inserisci contratto',
                denyButtonText: 'Attiva App',
                denyButtonColor: '#7066e0',
                cancelButtonText: 'Annulla',
                allowOutsideClick: false,
            });
    
            if (result.isConfirmed) {
                window.location.href = `/dipendenti/${employee.employee.cf}/edit?tab=2`;
                return;
            } else if (!result.isDenied) {
                return;
            }
        }
    
        Swal.fire({
            title: 'Attendere...',
            allowOutsideClick: false,
            didOpen: () => {
                Swal.showLoading();
            }
        });
    
        try {
            await changeAppEnable(token, employee.employee.cf, status);
    
            setEmployee((prev) => ({
                ...prev,
                employee: {
                    ...prev.employee,
                    app_activate: !prev.employee.app_activate
                }
            }));
    
            Swal.fire({
                icon: 'success',
                title: 'Salvato!',
                text: employee.employee.app_activate
                    ? 'L\'accesso all\'app è stato disattivato!'
                    : 'L\'accesso all\'app è stato attivato!',
            });
    
        } catch (error) {
            console.error('Errore nel cambio dello stato dell\'app:', error);
    
            Swal.fire({
                icon: 'error',
                title: 'Errore!',
                text: 'Si è verificato un errore durante il salvataggio',
            });
        }    
    };

    const handleEditNotes = () => {
        setNotesDraft(employee?.employee?.notes ?? "");
        setIsEditingNotes(true);
    };

    const handleCancelNotes = () => {
        setNotesDraft(employee?.employee?.notes ?? "");
        setIsEditingNotes(false);
    };

    const handleSaveNotes = async () => {
        if (!employee?.employee?.id || isSavingNotes) return;

        setIsSavingNotes(true);

        try {
            const payload = {
                notes: notesDraft,
            };

            await updateAnagraficaEmployee(token, employee.employee.id, payload);
            setEmployee((prev) => ({ ...prev, employee: {...prev.employee, notes: notesDraft}, }));
            setIsEditingNotes(false);

            Swal.fire({
                icon: "success",
                title: "Note aggiornate!",
                text: "Le note del dipendente sono state salvate correttamente.",
                showConfirmButton: false,
                timer: 1200,
            });
        } catch (error) {
            console.error("Errore durante il salvataggio delle note:", error);
            Swal.fire({
                icon: "error",
                title: "Errore",
                text: "Impossibile aggiornare le note del dipendente.",
            });
        } finally {
            setIsSavingNotes(false);
        }
    };

    if(isLoading)
        return <div className="relative block w-full rounded-lg  p-12 text-center hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2">
            <div role="status">
                <svg className="inline w-10 h-10 mr-2 text-gray-200 animate-spin dark:text-grey-100 fill-indigo-700" viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z" fill="currentColor" />
                    <path d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z" fill="currentFill" />
                </svg>
                <span className="sr-only">Loading...</span>
            </div>
            <span className="mt-2 block text-sm font-semibold text-gray-900">Caricamento...</span>
        </div>
    if (isError) return <div>Error loading data</div>;


    return (
        <div className="flex flex-col min-h-screen">
            <Popover as="header" className='bg-indigo-200'>
                <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:max-w-7xl lg:px-8">
                    <div className="relative flex flex-wrap items-center justify-center lg:justify-between">
                        <div className="w-full -5 lg:border-t lg:border-white lg:border-opacity-20">
                            <div className="flex justify-center my-2 py-2">
                                <h2 className="text-center text-2xl font-semibold text-indigo-800">
                                    {employee ? (
                                        (employee.last_name || employee.first_name) ? (
                                            `${employee.last_name || ''} ${employee.first_name || ''}`.trim()
                                        ) : (
                                            employee.cf
                                        )
                                    ) : (
                                        "Caricando le informazioni..."
                                    )}
                                </h2>
                            </div>
                        </div>
                    </div>
                </div>
            </Popover>
            <main className="mt-10 pb-8 flex-grow">
                <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:max-w-7xl lg:px-8">
                    <div className="flex items-center my-4 justify-between">
                        <div>
                            <BackButton prevLocation={`/dipendenti/${codiceFiscale}`} />
                        </div>
                    </div>

                    {isLoading ? (
                        <p>Caricamento in corso...</p>
                    ) : isError ? (
                        <p>Errore nel caricamento dei dati.</p>
                    ) : (
                        employee && (
                            <>
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center">
                                        <div className="uppercase block text-indigo-700 text-md font-bold mr-4">Dati accesso app</div>
                                        <div className="pt-2 pb-4">
                                            <Link
                                                to={`/dipendenti/${codiceFiscale}/edit`}
                                                className="relative inline-flex items-center rounded-md bg-indigo-600 px-2 py-1 text-xs font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                                            >
                                                Modifica
                                            </Link>
                                        </div>
                                    </div>
                                    <div className="flex items-center">
                                        <div className="uppercase block text-gray-700 text-md font-bold mr-3">App Attiva</div>
                                        <div>{employee.employee.app_activate ? 
                                            (
                                                <span className="mr-2 font-bold text-indigo-600 border border-indigo-600 px-1 rounded-md">SI</span>
                                            ) : (
                                                <span className="mr-2 font-bold text-gray-600 border border-gray-600 px-1 rounded-md">NO</span>
                                            )}
                                        </div>
                                        {/* {employee.employee.app_activate ? 
                                            (
                                                <button 
                                                    onClick={handleAppStatusChange}
                                                    className="relative inline-flex items-center rounded-md bg-indigo-600 px-2 py-1 text-xs font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 ml-2">
                                                    Disattiva Accesso App
                                                </button>
                                            ) : (
                                                <button 
                                                    onClick={handleAppStatusChange}
                                                    className="relative inline-flex items-center rounded-md bg-indigo-600 px-2 py-1 text-xs font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 ml-2">
                                                    Attiva Accesso App
                                                </button>
                                            )
                                        } */}
                                    </div>
                                </div>
                                <form className="grid grid-cols-1 md:grid-cols-3 gap-4 pb-6">
                                    <div className="flex flex-col justify-between h-full">
                                        <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="firstName">
                                            Nome
                                        </label>
                                        <div className="border border-gray-300 shadow-md h-10 uppercase rounded-md w-full py-2 px-3 text-gray-700 focus:outline-none focus:ring-offset-none overflow-hidden text-overflow-ellipsis whitespace-nowrap">
                                            {employee.employee.first_name}
                                        </div>
                                    </div>
                                    <div className="flex flex-col justify-between h-full">
                                        <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="lastName">
                                            Cognome
                                        </label>
                                        <div className="border border-gray-300 shadow-md h-10 uppercase rounded-md w-full py-2 px-3 text-gray-700 focus:outline-none focus:ring-offset-none overflow-hidden text-overflow-ellipsis whitespace-nowrap">
                                            {employee.employee.last_name}
                                        </div>
                                    </div>
                                    <div className="flex flex-col justify-between h-full">
                                    <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="cf">
                                            Codice Fiscale
                                        </label>
                                        <div className="border border-gray-300 shadow-md h-10 uppercase rounded-md w-full py-2 px-3 text-gray-700 focus:outline-none focus:ring-offset-none overflow-hidden text-overflow-ellipsis whitespace-nowrap">
                                            {employee.employee.cf}
                                        </div>
                                    </div>
                                    <div className="flex flex-col justify-between h-full">
                                        <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="email">
                                            Email
                                        </label>
                                        <div className="border border-gray-300 shadow-md h-10 uppercase rounded-md w-full py-2 px-3 text-gray-700 focus:outline-none focus:ring-offset-none overflow-hidden text-overflow-ellipsis whitespace-nowrap">
                                            {employee.employee.email_auth ? employee.employee.email_auth : ''}
                                        </div>
                                    </div>
                                </form>

                                <hr />

                                {employee && (
                                    <>
                                        <div className="flex items-center pt-6">
                                            <div className="uppercase block text-indigo-700 text-md font-bold mr-4">Lavoratore</div>
                                            <div className="pt-2 pb-4">
                                                <Link
                                                    to={`/dipendenti/${codiceFiscale}/edit?tab=1`}
                                                    className="relative inline-flex items-center rounded-md bg-indigo-600 px-2 py-1 text-xs font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                                                >
                                                    Modifica
                                                </Link>
                                            </div>
                                        </div>

                                        <form className="grid grid-cols-1 md:grid-cols-3 gap-4 pb-6">
                                            <div className="flex flex-col justify-between h-full">
                                                <label className="block text-gray-700 text-sm font-bold mb-2">
                                                    Sesso
                                                </label>
                                                <div className="border border-gray-300 shadow-md h-10 uppercase rounded-md w-full py-2 px-3 text-gray-700 focus:outline-none focus:ring-offset-none overflow-hidden text-overflow-ellipsis whitespace-nowrap">
                                                    {employee.employee.sex}
                                                </div>
                                            </div>
                                            <div className="flex flex-col justify-between h-full">
                                                <label className="block text-gray-700 text-sm font-bold mb-2">
                                                    Data di Nascita
                                                </label>
                                                <div className="border border-gray-300 shadow-md h-10 uppercase rounded-md w-full py-2 px-3 text-gray-700 focus:outline-none focus:ring-offset-none overflow-hidden text-overflow-ellipsis whitespace-nowrap">
                                                    {formatData(employee.employee.birth_date)}
                                                </div>
                                            </div>
                                            <div className="flex flex-col justify-between h-full">
                                                <label className="block text-gray-700 text-sm font-bold mb-2">
                                                    Luogo di Nascita
                                                </label>
                                                <div className="border border-gray-300 shadow-md h-10 uppercase rounded-md w-full py-2 px-3 text-gray-700 focus:outline-none focus:ring-offset-none overflow-hidden text-overflow-ellipsis whitespace-nowrap">
                                                    {employee.employee.birth_place}
                                                </div>
                                            </div>
                                            <div className="flex flex-col justify-between h-full">
                                                <label className="block text-gray-700 text-sm font-bold mb-2">
                                                    Comune Domicilio
                                                </label>
                                                <div className="border border-gray-300 shadow-md h-10 uppercase rounded-md w-full py-2 px-3 text-gray-700 focus:outline-none focus:ring-offset-none overflow-hidden text-overflow-ellipsis whitespace-nowrap">
                                                    {employee?.employee?.domicilio_comune 
                                                        ? `${employee.employee.domicilio_comune.codice_catastale} - ${employee.employee.domicilio_comune.name}` 
                                                        : ''} 
                                                </div>
                                            </div>
                                            <div className="flex flex-col justify-between h-full">
                                                <label className="block text-gray-700 text-sm font-bold mb-2">
                                                    CAP Domicilio
                                                </label>
                                                <div className="border border-gray-300 shadow-md h-10 uppercase rounded-md w-full py-2 px-3 text-gray-700 focus:outline-none focus:ring-offset-none overflow-hidden text-overflow-ellipsis whitespace-nowrap">
                                                    {employee?.employee?.domicilio_cap}
                                                </div>
                                            </div>
                                            <div className="flex flex-col justify-between h-full">
                                                <label className="block text-gray-700 text-sm font-bold mb-2">
                                                    Indirizzo Domicilio
                                                </label>
                                                <div className="border border-gray-300 shadow-md h-10 uppercase rounded-md w-full py-2 px-3 text-gray-700 focus:outline-none focus:ring-offset-none overflow-hidden text-overflow-ellipsis whitespace-nowrap">
                                                    {employee?.employee?.domicilio_indirizzo}
                                                </div>
                                            </div>
                                            <div className="flex flex-col justify-between h-full">
                                                <label className="block text-gray-700 text-sm font-bold mb-2">
                                                    Cittadinanza
                                                </label>
                                                <div className="border border-gray-300 shadow-md h-10 uppercase rounded-md w-full py-2 px-3 text-gray-700 focus:outline-none focus:ring-offset-none overflow-hidden text-overflow-ellipsis whitespace-nowrap">
                                                    {employee?.employee?.citizenship 
                                                        ? `${employee?.employee?.citizenship.code_mf} - ${employee?.employee?.citizenship.des_citizenship}` 
                                                        : ''}
                                                </div>
                                            </div>
                                            <div className="flex flex-col justify-between h-full">
                                                <label className="block text-gray-700 text-sm font-bold mb-2">
                                                    Livello Istruzione
                                                </label>
                                                <div className="border border-gray-300 shadow-md h-10 uppercase rounded-md w-full py-2 px-3 text-gray-700 focus:outline-none focus:ring-offset-none overflow-hidden text-overflow-ellipsis whitespace-nowrap">
                                                    {employee?.employee?.education_level?.description}
                                                </div>
                                            </div>
                                            <div className="flex flex-col justify-between h-full">
                                                <label className="block text-gray-700 text-sm font-bold mb-2">
                                                    Sede di Lavoro
                                                </label>
                                                <div className="border border-gray-300 shadow-md h-10 uppercase rounded-md w-full py-2 px-3 text-gray-700 focus:outline-none focus:ring-offset-none overflow-hidden text-overflow-ellipsis whitespace-nowrap">
                                                    {employee?.employee?.location ? (
                                                        `${employee.employee.location.name || ''} ${employee.employee.location.address ? `- ${employee.employee.location.address}` : ''}`
                                                    ) : (
                                                        ''
                                                    )}
                                                </div>
                                            </div>
                                            <div className="flex flex-col justify-between h-full">
                                                <label className="block text-gray-700 text-sm font-bold mb-2">
                                                    Telefono
                                                </label>
                                                <div className="border border-gray-300 shadow-md h-10 uppercase rounded-md w-full py-2 px-3 text-gray-700 focus:outline-none focus:ring-offset-none overflow-hidden text-overflow-ellipsis whitespace-nowrap">
                                                    {employee?.employee?.phone_number || ''}
                                                </div>
                                            </div>
                                            <div className="flex flex-col justify-between h-full">
                                                <label className="block text-gray-700 text-sm font-bold mb-2">
                                                    Email
                                                </label>
                                                <div className="border border-gray-300 shadow-md h-10 uppercase rounded-md w-full py-2 px-3 text-gray-700 focus:outline-none focus:ring-offset-none overflow-hidden text-overflow-ellipsis whitespace-nowrap">
                                                    {employee?.employee?.email || ''}
                                                </div>
                                            </div>
                                            <div className="flex flex-col justify-between h-full">
                                                <label className="block text-gray-700 text-sm font-bold mb-2">
                                                    Iban
                                                </label>
                                                <div className="border border-gray-300 shadow-md h-10 uppercase rounded-md w-full py-2 px-3 text-gray-700 focus:outline-none focus:ring-offset-none overflow-hidden text-overflow-ellipsis whitespace-nowrap">
                                                    {employee?.employee?.iban || ''}
                                                </div>
                                            </div>
                                            <div className="flex flex-col justify-between h-full">
                                                <label className="block text-gray-700 text-sm font-bold mb-2">
                                                    ID Gestionale Esterno
                                                </label>
                                                <div className="border border-gray-300 shadow-md h-10 uppercase rounded-md w-full py-2 px-3 text-gray-700 focus:outline-none focus:ring-offset-none overflow-hidden text-overflow-ellipsis whitespace-nowrap">
                                                    {employee?.employee?.external_reference || ''}
                                                </div>
                                            </div>
                                            <div className="flex flex-col justify-between h-full">
                                                <label className="block text-gray-700 text-sm font-bold mb-2">
                                                    ID Sede Lavoro Gestionale Esterno
                                                </label>
                                                <div className="border border-gray-300 shadow-md h-10 uppercase rounded-md w-full py-2 px-3 text-gray-700 focus:outline-none focus:ring-offset-none overflow-hidden text-overflow-ellipsis whitespace-nowrap">
                                                    {employee?.employee?.external_location_reference || ''}
                                                </div>
                                            </div>
                                            {/* NOTE DIPENDENTE */}
                                            <div className="md:col-span-3 flex flex-col mt-2">
                                                <div className="flex flex-wrap items-center gap-3 mb-2">
                                                    <label htmlFor="employee-notes" className="uppercase block text-indigo-700 text-md font-bold">
                                                        Note
                                                    </label>
                                                    {!isEditingNotes ? (
                                                        <button
                                                            type="button"
                                                            onClick={handleEditNotes}
                                                            className="relative inline-flex items-center rounded-md bg-indigo-600 px-2 py-1 text-xs font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                                                        >
                                                            Modifica
                                                        </button>
                                                    ) : (
                                                        <div className="flex items-center gap-2">
                                                            <button
                                                                type="button"
                                                                onClick={handleCancelNotes}
                                                                disabled={isSavingNotes}
                                                                className="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-2 py-1 text-xs font-semibold text-gray-700 shadow-sm hover:bg-gray-50 disabled:opacity-50"
                                                            >
                                                                Annulla
                                                            </button>
                                                            <button
                                                                type="button"
                                                                onClick={handleSaveNotes}
                                                                disabled={isSavingNotes || notesDraft === (employee?.employee?.notes ?? "")}
                                                                className="relative inline-flex items-center rounded-md bg-indigo-600 px-2 py-1 text-xs font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:cursor-not-allowed disabled:opacity-50"
                                                            >
                                                                {isSavingNotes ? "Salvataggio..." : "Salva"}
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>

                                                {/* CONTENUTO NOTE */}
                                                {isEditingNotes ? (
                                                    <textarea
                                                        id="employee-notes"
                                                        rows={4}
                                                        value={notesDraft}
                                                        onChange={(e) => setNotesDraft(e.target.value)}
                                                        disabled={isSavingNotes}
                                                        placeholder="Inserisci eventuali note relative al dipendente..."
                                                        className="w-full min-h-[110px] resize-y rounded-md border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-700 shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-60"
                                                    />
                                                ) : (
                                                    <div className="min-h-[85px] w-full whitespace-pre-wrap break-words rounded-md border border-gray-300 bg-gray-50 px-3 py-3 text-sm text-gray-700 shadow-sm">
                                                        {employee?.employee?.notes?.trim()
                                                            ? employee.employee.notes
                                                            : (
                                                                <span className="italic text-gray-400">
                                                                    Nessuna nota inserita
                                                                </span>
                                                            )
                                                        }
                                                    </div>
                                                )}

                                            </div>
                                        </form>
                                    </>
                                )}
                               
                               {employee?.employee?.is_ue === false && (
                                    <>
                                        <hr />
    
                                        <div className="flex items-center pt-6">
                                            <div className="uppercase block text-indigo-700 text-md font-bold mr-4">Lavoratore Extra UE</div>
                                            <div className="pt-2 pb-4">
                                                <Link
                                                    to={`/dipendenti/${codiceFiscale}/edit?tab=1`}
                                                    className="relative inline-flex items-center rounded-md bg-indigo-600 px-2 py-1 text-xs font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                                                >
                                                    Modifica
                                                </Link>
                                            </div>
                                        </div>
        
                                        <form className="grid grid-cols-1 md:grid-cols-3 gap-4 pb-6">
                                            <div className="flex flex-col justify-between h-full">
                                                <label className="block text-gray-700 text-sm font-bold mb-2">
                                                    Titolo di Soggiorno
                                                </label>
                                                <div className="border border-gray-300 shadow-md h-10 uppercase rounded-md w-full py-2 px-3 text-gray-700 focus:outline-none focus:ring-offset-none overflow-hidden text-overflow-ellipsis whitespace-nowrap">
                                                    {employee.employee.residence_permit_type 
                                                        ? `${employee.employee.residence_permit_type.code} - ${employee.employee.residence_permit_type.description}` 
                                                        : ''}
                                                </div>
                                            </div>
                                            <div className="flex flex-col justify-between h-full">
                                                <label className="block text-gray-700 text-sm font-bold mb-2">
                                                    Numero Permesso di Soggiorno
                                                </label>
                                                <div className="border border-gray-300 shadow-md h-10 uppercase rounded-md w-full py-2 px-3 text-gray-700 focus:outline-none focus:ring-offset-none overflow-hidden text-overflow-ellipsis whitespace-nowrap">
                                                    {employee.employee.residence_permit_number}
                                                </div>
                                            </div>
                                            <div className="flex flex-col justify-between h-full">
                                            <label className="block text-gray-700 text-sm font-bold mb-2">
                                                    Motivo Titolo di Soggiorno
                                                </label>
                                                <div className="border border-gray-300 shadow-md h-10 uppercase rounded-md w-full py-2 px-3 text-gray-700 focus:outline-none focus:ring-offset-none overflow-hidden text-overflow-ellipsis whitespace-nowrap">
                                                    {employee.employee.residence_permit_reason 
                                                        ? `${employee.employee.residence_permit_reason.code} - ${employee.employee.residence_permit_reason.description}` 
                                                        : ''}
                                                </div>
                                            </div>
                                            <div className="flex flex-col justify-between h-full">
                                                <label className="block text-gray-700 text-sm font-bold mb-2">
                                                    Scadenza Titolo di Soggiorno
                                                </label>
                                                <div className="border border-gray-300 shadow-md h-10 uppercase rounded-md w-full py-2 px-3 text-gray-700 focus:outline-none focus:ring-offset-none overflow-hidden text-overflow-ellipsis whitespace-nowrap">
                                                    {formatData(employee.employee.residence_permit_end || '')}
                                                </div>
                                            </div>
                                            <div className="flex flex-col justify-between h-full">
                                                <label className="block text-gray-700 text-sm font-bold mb-2">
                                                    Questura Rilascio Titolo di Soggiorno
                                                </label>
                                                <div className="border border-gray-300 shadow-md h-10 uppercase rounded-md w-full py-2 px-3 text-gray-700 focus:outline-none focus:ring-offset-none overflow-hidden text-overflow-ellipsis whitespace-nowrap">
                                                    {employee.employee.residence_permit_police_hq 
                                                        ? `${employee.employee.residence_permit_police_hq.code} - ${employee.employee.residence_permit_police_hq.description}` 
                                                        : ''}
                                                </div>
                                            </div>
                                        </form>
                                    </>
                                )}
                            </>
                        )
                    )}
                </div>
            </main>
            <Footer />
        </div>
    );
}

  